import { Link } from "react-router";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useInternalLink } from "@/hooks/use-internal-link";
import { formatDateBR, formatDateOnly } from "@/utils/dateUtils";
import { getVagaStatusInfo } from "@/utils/vaga-status";
import type { VagaListagem } from "@/types/vaga";
import { decisaoIso, formatDeltaAbrev, formatVerba } from "./vagaRowUtils";

interface VagasListRowProps {
  vaga: VagaListagem;
}

export function VagasListRow({ vaga }: VagasListRowProps) {
  const linkProps = useInternalLink();
  const href = `/vagas/${vaga.id}`;
  const statusInfo = getVagaStatusInfo(vaga.status);
  const decisao = decisaoIso(vaga);
  const delta = decisao && vaga.created_at ? formatDeltaAbrev(vaga.created_at, decisao) : null;

  return (
    <tr
      {...linkProps(href)}
      className="cursor-pointer border-b transition-colors hover:bg-muted/50"
    >
      <td className="px-3 py-2 align-middle text-sm">
        <div className="flex items-baseline gap-2">
          <Link
            to={href}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[180px] truncate font-semibold hover:underline"
          >
            {vaga.agencia || "—"}
          </Link>
          <span className="shrink-0 font-mono text-[11px] font-normal text-muted-foreground" title={vaga.id}>
            #{vaga.id.slice(0, 6)}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 align-middle text-xs text-muted-foreground">
        <div className="max-w-[220px] truncate">
          {vaga.areas || "—"}
          {vaga.descricao_curta && (
            <span className="text-slate-400" title={vaga.descricao_curta}>
              {" · "}{vaga.descricao_curta}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-middle text-xs whitespace-nowrap">{formatVerba(vaga)}</td>
      <td className="px-3 py-2 align-middle">
        <Badge variant="outline" className={cn("whitespace-nowrap", statusInfo?.badgeClass)}>
          {statusInfo?.title ?? vaga.status}
        </Badge>
      </td>
      <td className="px-3 py-2 align-middle text-xs whitespace-nowrap">
        {vaga.deadline_preenchimento ? formatDateBR(vaga.deadline_preenchimento) : "—"}
      </td>
      <td className="px-3 py-2 align-middle text-xs whitespace-nowrap">
        <span className="text-blue-700">{vaga.qtd_shortlist ?? 0}</span>
        <span className="text-muted-foreground"> / </span>
        <span className="text-emerald-700">{vaga.qtd_aprovados_kanban ?? 0}</span>
        {vaga.aprovado_kanban_freelancer_nome && (
          <span
            className="ml-1.5 text-[11px] text-muted-foreground"
            title={vaga.aprovado_kanban_freelancer_nome}
          >
            {vaga.aprovado_kanban_freelancer_nome}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-middle text-xs text-muted-foreground whitespace-nowrap">
        {vaga.created_at ? formatDateOnly(vaga.created_at) : "—"}
      </td>
      <td className="px-3 py-2 align-middle text-xs whitespace-nowrap">
        {decisao ? (
          <span className="text-muted-foreground">
            {formatDateOnly(decisao)}
            {delta && <span className="ml-1.5 text-[11px] text-slate-400">({delta})</span>}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex justify-end">
          <Button asChild variant="ghost" size="icon" className="h-7 w-7">
            <Link to={href} onClick={(e) => e.stopPropagation()} title="Abrir detalhes">
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </td>
    </tr>
  );
}
